import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * URL helpers for LLM question navigation in the composer.
 *
 * The route `/composer/courses/:courseId/llm-questions/:questionId` encodes the active
 * question id. A URL hash (`#info|#tests|#prompts`) selects which section to scroll
 * to / highlight in the sidebar tree.
 *
 * Legacy compatibility: nothing writes the `#llm-q-<id>` hash, but existing links that
 * use it still resolve through `parseLegacyQuestionHash`.
 */
export const LLM_QUESTION_SECTION_IDS = Object.freeze({
  info: "info",
  tests: "tests",
  prompts: "prompts",
});

const VALID_SECTIONS = new Set(Object.values(LLM_QUESTION_SECTION_IDS));

/** Legacy hash prefix still honoured when the app loads from an old bookmark. */
export const LLM_QUESTION_HASH_PREFIX = "llm-q-";

export const getSectionFromHash = (hash) => {
  const raw = (hash || "").replace(/^#/, "").trim();
  if (!raw) return null;
  return VALID_SECTIONS.has(raw) ? raw : null;
};

export const parseLegacyQuestionHash = (hash) => {
  const raw = (hash || "").replace(/^#/, "");
  if (!raw.startsWith(LLM_QUESTION_HASH_PREFIX)) return null;
  const id = raw.slice(LLM_QUESTION_HASH_PREFIX.length);
  return id || null;
};

/**
 * Section-aware navigation hook.
 * Returns the current section (info/tests/prompts) from the URL hash plus a setter
 * that updates the hash without triggering a full route re-render.
 */
const useLlmQuestionSectionHash = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const section = getSectionFromHash(location.hash);

  const setSection = useCallback(
    (nextSection) => {
      const resolved = VALID_SECTIONS.has(nextSection) ? nextSection : null;
      const nextHash = resolved ? `#${resolved}` : "";
      if ((location.hash || "") === nextHash) return;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
        },
        { replace: true }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  return [section, setSection];
};

export default useLlmQuestionSectionHash;
